import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCart } from '../context/CartContext';
import DatePicker from 'react-datepicker';
import "react-datepicker/dist/react-datepicker.css";
import { differenceInDays, format } from 'date-fns';
import { loadStripe } from '@stripe/stripe-js';
import { Elements } from '@stripe/react-stripe-js';
import PaymentForm from '../components/PaymentForm';
import { STRIPE_PUBLISHABLE_KEY } from '../config/stripe';
import Confetti from 'react-confetti';

const stripePromise = loadStripe(STRIPE_PUBLISHABLE_KEY);

const Checkout = () => {
  const navigate = useNavigate();
  const { cartItems, getCartTotal, clearCart } = useCart();

  const [currentStep, setCurrentStep] = useState(1);
  const [clientSecret, setClientSecret] = useState('');
  const [deliveryMethod, setDeliveryMethod] = useState('delivery');
  const [deliveryFeeCalculated, setDeliveryFeeCalculated] = useState(false);
  const [calculatingFee, setCalculatingFee] = useState(false);
  const [deliveryDistance, setDeliveryDistance] = useState(null);
  const [deliveryFeeAmount, setDeliveryFeeAmount] = useState(0); // Default
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [showTermsError, setShowTermsError] = useState(false); // ✅ Add this
  const [showFormError, setShowFormError] = useState(false); // ✅ Add this\
  const [missingFields, setMissingFields] = useState([]); // ✅ Add this to show which fields are missing
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [orderNumber, setOrderNumber] = useState('');
  const [showConfetti, setShowConfetti] = useState(false);
  const [showDeliveryFeeModal, setShowDeliveryFeeModal] = useState(false);
  const [calculatedAddress, setCalculatedAddress] = useState(null);


  const API_URL = 'https://dear-bea-website.onrender.com';

// Form state
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    address: '',
    suburb: '',
    postcode: '',
    specialInstructions: ''
  });

  const [deliveryDate, setDeliveryDate] = useState(null);
  const [deliveryTime, setDeliveryTime] = useState('');
  const [errors, setErrors] = useState({});

  // ✅ Clear special instructions when switching to pickup
useEffect(() => {
  if (deliveryMethod === 'pickup') {
    setFormData(prev => ({
      ...prev,
      specialInstructions: ''
    }));
  }
}, [deliveryMethod]);


  const isRushDelivery = () => {
    if (!deliveryDate) return false;
    const daysUntilDelivery = differenceInDays(deliveryDate, new Date());
    return daysUntilDelivery < 14;
  };

 // ✅ UPDATED FUNCTION - MATCHES BACKEND EXACTLY
const getRushFee = () => {
  const hasValentineProducts = cartItems.some(item => item.category === 'valentine');
  if (hasValentineProducts) return 0;

  const hasMothersDayProducts = cartItems.some(item => item.category === 'mothersday');
  if (hasMothersDayProducts) return 0;

  if (!deliveryDate) return 0;
  
  // ✅ USE SAME CALCULATION AS BACKEND
   // ✅ Convert to UTC to avoid timezone issues
  const deliveryUTC = Date.UTC(
    deliveryDate.getFullYear(), 
    deliveryDate.getMonth(), 
    deliveryDate.getDate()
  );
  const todayUTC = Date.UTC(
    new Date().getFullYear(), 
    new Date().getMonth(), 
    new Date().getDate()
  );
  const daysUntilDelivery = Math.round((deliveryUTC - todayUTC) / (1000 * 60 * 60 * 24));

   console.log('🎯 FRONTEND: Days until delivery (UTC):', daysUntilDelivery);
  console.log('🎯 FRONTEND: Delivery date:', deliveryDate.toISOString());
  console.log('🎯 FRONTEND: Today:', new Date().toISOString());
  console.log('🎯 FRONTEND: Subtotal:', subtotal);
  
  // Less than 3 days
  if (daysUntilDelivery < 3) {
    console.log('🎯 FRONTEND: Applying 25% fee');
    return subtotal * 0.25;
  }
  // Less than 7 days  
  if (daysUntilDelivery < 7) {
    console.log('🎯 FRONTEND: Applying 20% fee');
    return subtotal * 0.20;
  }
  // Less than 14 days
  if (daysUntilDelivery < 14) {
    console.log('🎯 FRONTEND: Applying 10% fee');
    return subtotal * 0.10;
  }
  
  console.log('🎯 FRONTEND: No rush fee');
  return 0;
};


    // Calculate delivery fee when address is complete
  const handleCalculateDeliveryFee = async () => {
    if (!formData.address || !formData.suburb || !formData.postcode) {
      alert('Please enter your complete delivery address first');
      return;
    }

    // ✅ Check if this exact address was already calculated
  const currentAddress = `${formData.address}|${formData.suburb}|${formData.postcode}`;
  
  if (calculatedAddress === currentAddress) {
    // Already calculated for this address - just show the modal again
    setShowDeliveryFeeModal(true);
    return;
  }

    setCalculatingFee(true);

    try {
      const response = await fetch(`${API_URL}/api/delivery/calculate-fee`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          address: formData.address,
          suburb: formData.suburb,
          postcode: formData.postcode,
        }),
      });

      const data = await response.json();

      if (data.success && data.deliverable) {
        setDeliveryFeeAmount(data.fee);
        setDeliveryDistance(data.distance);
        setDeliveryFeeCalculated(true);
        setCalculatedAddress(currentAddress); // ✅ Store the calculated address
        
        // Show success message
        setShowDeliveryFeeModal(true);
      } else {
        setDeliveryFeeCalculated(false);
        alert(data.message || 'Unable to deliver to this address');
      }

    } catch (error) {
      console.error('Error calculating delivery fee:', error);
      alert('Failed to calculate delivery fee. Please try again.');
    } finally {
      setCalculatingFee(false);
    }
  };

  const deliveryFee = deliveryMethod === 'delivery' ? deliveryFeeAmount : 0;

  const subtotal = getCartTotal();
  const rushFee = getRushFee();  // ✅ CHANGED
  const total = subtotal + deliveryFee + rushFee;

  useEffect(() => {
    if (currentStep === 2 && !clientSecret) {
      createPaymentIntent();
    }
  }, [currentStep]);

  const createPaymentIntent = async () => {
    try {
      console.log('Creating payment intent for amount:', total);
      
      const response = await fetch(`${API_URL}/api/create-payment-intent`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          amount: total,
          customerInfo: formData,
          orderDetails: {
            deliveryDate: deliveryDate ? format(deliveryDate, 'yyyy-MM-dd') : null,
            deliveryTime,
            items: cartItems,
            subtotal,
            deliveryFee,
            rushFee,
          },
        }),
      });

      const data = await response.json();
      
      if (data.clientSecret) {
        setClientSecret(data.clientSecret);
        console.log('✅ Payment Intent created:', data.paymentIntentId);
      } else {
        console.error('❌ Error creating payment intent:', data.error);
      }
    } catch (error) {
      console.error('❌ Network error:', error);
    }
  };

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });

    // ✅ Reset delivery fee calculation if address fields change
  if (['address', 'suburb', 'postcode'].includes(e.target.name)) {
    setDeliveryFeeCalculated(false);
    setCalculatedAddress(null);
  }

    if (errors[e.target.name]) {
      setErrors({ ...errors, [e.target.name]: '' });
    }
  };

  const validateForm = () => {
    const newErrors = {};
    const missing = []; // Track which fields are missing


      // Contact Information
  if (!formData.firstName.trim()) {
    newErrors.firstName = 'First name is required';
    missing.push('First Name');
  }
  if (!formData.lastName.trim()) {
    newErrors.lastName = 'Last name is required';
    missing.push('Last Name');
  }
  if (!formData.email.trim()) {
    newErrors.email = 'Email is required';
    missing.push('Email');
  } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
    newErrors.email = 'Please enter a valid email';
    missing.push('Valid Email');
  }
  if (!formData.phone.trim()) {
  newErrors.phone = 'Phone is required';
  missing.push('Phone');
} else if (!/^[0-9\s\+\-\(\)]+$/.test(formData.phone)) {
  newErrors.phone = 'Please enter a valid phone number';
  missing.push('Valid Phone');
}
  
  // Delivery Address (only if delivery method)
  if (deliveryMethod === 'delivery') {
    if (!formData.address.trim()) {
      newErrors.address = 'Address is required';
      missing.push('Street Address');
    }
    if (!formData.suburb.trim()) {
      newErrors.suburb = 'Suburb is required';
      missing.push('Suburb');
    }
    if (!formData.postcode.trim()) {
  newErrors.postcode = 'Postcode is required';
  missing.push('Postcode');
} else if (!/^\d{4}$/.test(formData.postcode)) {
  newErrors.postcode = 'Postcode must be 4 digits';
  missing.push('Valid Postcode');
}

    // ✅ Check if delivery fee has been calculated
    if (!deliveryFeeCalculated) {
      newErrors.deliveryFee = 'Please calculate delivery fee';
      missing.push('Delivery Fee Calculation');
    }
  }
  
  // Date and Time
  if (!deliveryDate) {
    newErrors.deliveryDate = 'Date is required';
    missing.push(deliveryMethod === 'delivery' ? 'Delivery Date' : 'Pickup Date');
  }
  const hasMothersDayProducts = cartItems.some(item => item.category === 'mothersday');
if (!deliveryTime && !hasMothersDayProducts) {
  newErrors.deliveryTime = 'Time is required';
  missing.push(deliveryMethod === 'delivery' ? 'Delivery Time' : 'Pickup Time');
}
     // ✅ ADD TERMS & CONDITIONS VALIDATION
  if (!termsAccepted) {
    newErrors.terms = 'You must agree to the Terms & Conditions';
  }

    setErrors(newErrors);
    setMissingFields(missing);

    return Object.keys(newErrors).length === 0;
  };

  const handleContinueToPayment = () => {

    if (!validateForm()) {
      setShowFormError(true); // Show the error modal

       // Scroll to first error field
    const firstErrorField = Object.keys(errors)[0];
    const element = document.querySelector(`[name="${firstErrorField}"]`) || 
                    document.getElementById('termsAgree');
      if (element) {
      setTimeout(() => {
        element.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 300);
    }
    return;
    }

    if (!termsAccepted) {
     setShowTermsError(true); // Show modal instead of alert
    // Scroll to terms checkbox
    document.getElementById('termsAgree')?.scrollIntoView({ 
      behavior: 'smooth', 
      block: 'center' 
    });
    return;
  }

  if (cartItems.some(item => item.category === 'mothersday') && !deliveryTime) {
  setDeliveryTime('To be coordinated');
}

    setCurrentStep(2);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handlePaymentSuccess = async (paymentIntentId) => {
    try {
      console.log('Payment successful! Saving order to database...');
      
      const response = await fetch(`${API_URL}/api/orders`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          customerInfo: formData,
          deliveryDate: format(deliveryDate, 'yyyy-MM-dd'),
          deliveryTime,
          deliveryMethod,
          items: cartItems,
          subtotal,
          deliveryFee,
          rushFee,
          total,
          paymentIntentId,
          specialInstructions: formData.specialInstructions,
        }),
      });

      const data = await response.json();
      
      if (data.success) {
        console.log('✅ Order saved! Order number:', data.orderNumber);

          
      console.log('🎊 About to set states...');
      console.log('🎊 Current showSuccessModal:', showSuccessModal);
      console.log('🎊 Current showConfetti:', showConfetti);
      // ✅ Show success modal with confetti
      setOrderNumber(data.orderNumber);
            console.log('✅ Set order number');

      setShowSuccessModal(true);
            console.log('✅ Set showSuccessModal to TRUE');

      setShowConfetti(true);
            console.log('✅ Set showConfetti to TRUE');

      
      // Stop confetti after 5 seconds
      setTimeout(() => {
                console.log('🎊 Stopping confetti...');
        setShowConfetti(false);
      }, 5000);

      } else {
        console.error('❌ Error saving order:', data.error);
        alert('Payment successful but there was an error saving your order. Please contact support.');
      }
    } catch (error) {
      console.error('❌ Network error:', error);
      alert('Payment successful but there was an error saving your order. Please contact support.');
    }
  };

  if (cartItems.length === 0) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-white via-pink-50 to-white flex items-center justify-center px-4">
        <div className="text-center">
          <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4">Your cart is empty</h2>
          <button
            onClick={() => navigate('/')}
            className="bg-rose-600 hover:bg-rose-700 text-white px-6 md:px-8 py-2.5 md:py-3 rounded-lg text-base md:text-lg font-semibold transition"
          >
            Continue Shopping
          </button>
        </div>
      </div>
    );
  }

  const timeSlots = [
    '9:00 AM - 11:00 AM',
    '11:00 AM - 1:00 PM',
    '1:00 PM - 3:00 PM',
    '3:00 PM - 5:00 PM',
    '5:00 PM - 7:00 PM'
  ];

  const appearance = {
    theme: 'stripe',
    variables: {
      colorPrimary: '#e11d48',
      colorBackground: '#ffffff',
      colorText: '#1f2937',
      colorDanger: '#dc2626',
      fontFamily: 'system-ui, sans-serif',
      spacingUnit: '4px',
      borderRadius: '8px',
    },
  };

  const options = {
    clientSecret,
    appearance,
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-white via-pink-50 to-white py-6 md:py-12">
      <div className="max-w-7xl mx-auto px-4">
        
        {/* ✅ Header with Steps - Responsive */}
        <div className="mb-6 md:mb-8">
          <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-6 md:mb-10 font-display">
            Checkout
          </h1>
          
          {/* ✅ Step Indicator - Responsive */}
          <div className="flex items-center gap-2 md:gap-4 mb-4 md:mb-6">
            <div className={`flex items-center gap-1 md:gap-2 ${currentStep === 1 ? 'text-rose-600' : 'text-gray-400'}`}>
              <div className={`w-8 h-8 md:w-10 md:h-10 rounded-full flex items-center justify-center font-bold text-sm md:text-base ${
                currentStep === 1 ? 'bg-rose-600 text-white' : 'bg-gray-200'
              }`}>
                1
              </div>
              <span className="font-semibold text-sm md:text-base">Details</span>
            </div>
            <div className="flex-1 h-1 bg-gray-200 rounded">
              <div className={`h-full rounded transition-all duration-500 ${
                currentStep === 2 ? 'bg-rose-600 w-full' : 'w-0'
              }`} />
            </div>
            <div className={`flex items-center gap-1 md:gap-2 ${currentStep === 2 ? 'text-rose-600' : 'text-gray-400'}`}>
              <div className={`w-8 h-8 md:w-10 md:h-10 rounded-full flex items-center justify-center font-bold text-sm md:text-base ${
                currentStep === 2 ? 'bg-rose-600 text-white' : 'bg-gray-200'
              }`}>
                2
              </div>
              <span className="font-semibold text-sm md:text-base">Payment</span>
            </div>
          </div>
        </div>

        {/* ✅ Grid - Stack on mobile */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 md:gap-8">
          
          {/* Left side - Form or Payment */}
          <div className="lg:col-span-2">
            
            {currentStep === 1 ? (
              // STEP 1: Customer Details
              <div className="space-y-4 md:space-y-6 lg:space-y-8">
                
                {/* ✅ Contact Information - Responsive */}
                <div className="bg-white rounded-xl md:rounded-2xl shadow-lg p-4 md:p-6 lg:p-8">
                  <h2 className="text-xl md:text-2xl font-bold text-gray-900 mb-4 md:mb-6">Contact Information</h2>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
                    <div>
                      <label className="block text-gray-700 font-semibold mb-2 text-sm md:text-base">First Name</label>
                      <input
                        type="text"
                        name="firstName"
                        value={formData.firstName}
                        onChange={handleInputChange}
                        className={`w-full px-3 md:px-4 py-2 md:py-3 border-2 rounded-lg focus:outline-none text-sm md:text-base ${
                          errors.firstName ? 'border-red-500' : 'border-gray-200 focus:border-rose-600'
                        }`}
                        placeholder="John"
                      />
                      {errors.firstName && <p className="text-red-500 text-xs md:text-sm mt-1">{errors.firstName}</p>}
                    </div>

                    <div>
                      <label className="block text-gray-700 font-semibold mb-2 text-sm md:text-base">Last Name</label>
                      <input
                        type="text"
                        name="lastName"
                        value={formData.lastName}
                        onChange={handleInputChange}
                        className={`w-full px-3 md:px-4 py-2 md:py-3 border-2 rounded-lg focus:outline-none text-sm md:text-base ${
                          errors.lastName ? 'border-red-500' : 'border-gray-200 focus:border-rose-600'
                        }`}
                        placeholder="Smith"
                      />
                      {errors.lastName && <p className="text-red-500 text-xs md:text-sm mt-1">{errors.lastName}</p>}
                    </div>

                    <div>
                      <label className="block text-gray-700 font-semibold mb-2 text-sm md:text-base">Email</label>
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        className={`w-full px-3 md:px-4 py-2 md:py-3 border-2 rounded-lg focus:outline-none text-sm md:text-base ${
                          errors.email ? 'border-red-500' : 'border-gray-200 focus:border-rose-600'
                        }`}
                        placeholder="john@example.com"
                      />
                      {errors.email && <p className="text-red-500 text-xs md:text-sm mt-1">{errors.email}</p>}
                    </div>

                    <div>
                      <label className="block text-gray-700 font-semibold mb-2 text-sm md:text-base">Phone</label>
                      <input
                        type="tel"
                        name="phone"
                        value={formData.phone}
                        onChange={handleInputChange}
                        pattern="[0-9\s\+\-\(\)]+"
                        className={`w-full px-3 md:px-4 py-2 md:py-3 border-2 rounded-lg focus:outline-none text-sm md:text-base ${
                          errors.phone ? 'border-red-500' : 'border-gray-200 focus:border-rose-600'
                        }`}
                        placeholder="04XX XXX XXX"
                      />
                      {errors.phone && <p className="text-red-500 text-xs md:text-sm mt-1">{errors.phone}</p>}
                    </div>
                  </div>
                </div>

                {/* ✅ Delivery Method Selection - Responsive */}
                <div className="bg-white rounded-xl md:rounded-2xl shadow-lg p-4 md:p-6 lg:p-8">
                  <h2 className="text-xl md:text-2xl font-bold text-gray-900 mb-4 md:mb-6">Delivery Method</h2>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-4">
                    {/* Delivery Option */}
                    <button
                      type="button"
                      onClick={() => setDeliveryMethod('delivery')}
                      className={`p-4 md:p-6 rounded-xl border-2 transition-all duration-300 ${
                        deliveryMethod === 'delivery'
                          ? 'border-rose-600 bg-rose-50'
                          : 'border-gray-200 hover:border-rose-300'
                      }`}
                    >
                      <div className="flex items-start gap-3 md:gap-4">
                        <div className={`w-5 h-5 md:w-6 md:h-6 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${
                          deliveryMethod === 'delivery' ? 'border-rose-600' : 'border-gray-300'
                        }`}>
                          {deliveryMethod === 'delivery' && (
                            <div className="w-2.5 h-2.5 md:w-3 md:h-3 rounded-full bg-rose-600"></div>
                          )}
                        </div>
                        <div className="text-left">
                          <div className="flex items-center gap-2 mb-1">
                            <svg className="w-5 h-5 md:w-6 md:h-6 text-rose-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4" />
                            </svg>
                            <h3 className="text-base md:text-lg font-bold text-gray-900">Delivery</h3>
                          </div>
                          <p className="text-xs md:text-sm text-gray-600 mb-1 md:mb-2">We'll deliver to your address</p>
                          <p className="text-xs md:text-sm font-semibold text-rose-600">Fee is calculated $2 per km from Blacktown</p>
                        </div>
                      </div>
                    </button>

                    {/* Pickup Option */}
                    <button
                      type="button"
                      onClick={() => setDeliveryMethod('pickup')}
                      className={`p-4 md:p-6 rounded-xl border-2 transition-all duration-300 ${
                        deliveryMethod === 'pickup'
                          ? 'border-rose-600 bg-rose-50'
                          : 'border-gray-200 hover:border-rose-300'
                      }`}
                    >
                      <div className="flex items-start gap-3 md:gap-4">
                        <div className={`w-5 h-5 md:w-6 md:h-6 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${
                          deliveryMethod === 'pickup' ? 'border-rose-600' : 'border-gray-300'
                        }`}>
                          {deliveryMethod === 'pickup' && (
                            <div className="w-2.5 h-2.5 md:w-3 md:h-3 rounded-full bg-rose-600"></div>
                          )}
                        </div>
                        <div className="text-left">
                          <div className="flex items-center gap-2 mb-1">
                            <svg className="w-5 h-5 md:w-6 md:h-6 text-rose-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                            </svg>
                            <h3 className="text-base md:text-lg font-bold text-gray-900">Pickup</h3>
                          </div>
                          <p className="text-xs md:text-sm text-gray-600 mb-1 md:mb-2">Pick up from Blacktown, Sydney NSW</p>
                          <p className="text-xs md:text-sm font-semibold text-green-600">Free</p>
                        </div>
                      </div>
                    </button>
                  </div>
                </div>

                {/* Delivery Address */}
  {deliveryMethod === 'delivery' && (
    <div className="bg-white rounded-xl md:rounded-2xl shadow-lg p-4 md:p-6 lg:p-8">
      <h2 className="text-xl md:text-2xl font-bold text-gray-900 mb-4 md:mb-6">Delivery Address</h2>
      
      <div className="space-y-4 md:space-y-6">
        <div>
          <label className="block text-gray-700 font-semibold mb-2 text-sm md:text-base">Street Address</label>
          <input
            type="text"
            name="address"
            value={formData.address}
            onChange={handleInputChange}
            className={`w-full px-3 md:px-4 py-2 md:py-3 border-2 rounded-lg focus:outline-none text-sm md:text-base ${
              errors.address ? 'border-red-500' : 'border-gray-200 focus:border-rose-600'
            }`}
            placeholder="123 Main Street"
          />
          {errors.address && <p className="text-red-500 text-xs md:text-sm mt-1">{errors.address}</p>}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
          <div>
            <label className="block text-gray-700 font-semibold mb-2 text-sm md:text-base">Suburb</label>
            <input
              type="text"
              name="suburb"
              value={formData.suburb}
              onChange={handleInputChange}
              className={`w-full px-3 md:px-4 py-2 md:py-3 border-2 rounded-lg focus:outline-none text-sm md:text-base ${
                errors.suburb ? 'border-red-500' : 'border-gray-200 focus:border-rose-600'
              }`}
              placeholder="Bondi Junction"
            />
            {errors.suburb && <p className="text-red-500 text-xs md:text-sm mt-1">{errors.suburb}</p>}
          </div>

          <div>
            <label className="block text-gray-700 font-semibold mb-2 text-sm md:text-base">Postcode</label>
            <input
              type="text"
              name="postcode"
              value={formData.postcode}
              onChange={handleInputChange}
              className={`w-full px-3 md:px-4 py-2 md:py-3 border-2 rounded-lg focus:outline-none text-sm md:text-base ${
                errors.postcode ? 'border-red-500' : 'border-gray-200 focus:border-rose-600'
              }`}
              placeholder="2000"
              maxLength="4"
            />
            {errors.postcode && <p className="text-red-500 text-xs md:text-sm mt-1">{errors.postcode}</p>}
          </div>
        </div>

        <div>
          <button
            type="button"
            onClick={handleCalculateDeliveryFee}
            disabled={calculatingFee || !formData.address || !formData.suburb || !formData.postcode}
            className="w-full bg-pink-400 hover:bg-pink-500 disabled:bg-gray-400 disabled:cursor-not-allowed text-white py-3 md:py-4 rounded-lg md:rounded-xl font-semibold transition flex items-center justify-center gap-2 text-sm md:text-base"
          >
            {calculatingFee ? 'Calculating Distance...' : deliveryFeeCalculated ? 'View Delivery Fee' : 'Calculate Delivery Fee'}
          </button>

          {deliveryFeeCalculated && deliveryDistance !== null && (
            <div className="mt-4 p-4 bg-green-50 border-2 border-green-200 rounded-lg">
              <p className="font-semibold text-green-800 text-sm md:text-base">Delivery Available!</p>
              <p className="text-xs md:text-sm text-green-700 mt-1">Distance: {deliveryDistance.toFixed(1)} km from Blacktown</p>
              <p className="text-lg md:text-xl font-bold text-green-800 mt-1">
                Delivery Fee: ${deliveryFeeAmount.toFixed(2)}
                {deliveryFeeAmount === 0 && ' 🎉 (FREE!)'}
              </p>
            </div>
          )}
        </div>

        <div>
          <label className="block text-gray-700 font-semibold mb-2 text-sm md:text-base">Special Instructions (Optional)</label>
          <textarea
            name="specialInstructions"
            value={formData.specialInstructions}
            onChange={handleInputChange}
            rows="3"
            className="w-full px-3 md:px-4 py-2 md:py-3 border-2 border-gray-200 rounded-lg focus:border-rose-600 focus:outline-none text-sm md:text-base"
            placeholder="e.g., Leave at front door, call on arrival..."
          />
        </div>
      </div>
    </div>
  )}

  

                                {/* ✅ Delivery/Pickup Schedule - Responsive */}
                {deliveryMethod === 'delivery' && (
  <div className="bg-white rounded-xl md:rounded-2xl shadow-lg p-4 md:p-6 lg:p-8">
    <h2 className="text-xl md:text-2xl font-bold text-gray-900 mb-4 md:mb-6">Delivery Schedule</h2>
    
    {/* Availability notice */}
    <div className="bg-blue-50 border-2 border-blue-200 rounded-lg p-3 md:p-4 mb-4">
      <div className="flex items-start gap-2">
        <svg className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
        <div>
          <p className="text-sm md:text-base text-blue-800 font-semibold">
            {cartItems.some(item => item.category === 'mothersday')
              ? 'Mother\'s Day deliveries: 8–10 May 2026 only'
              : 'Deliveries available Wednesdays & Saturdays only'}
          </p>
          <p className="text-xs md:text-sm text-blue-700 mt-1">
            If your preferred date is unavailable, please contact us to inquire about special arrangements.
          </p>
        </div>
      </div>
    </div>
    
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
      <div>
        <label className="block text-gray-700 font-semibold mb-2 text-sm md:text-base">Delivery Date</label>
        <DatePicker
          selected={deliveryDate}
          onChange={(date) => setDeliveryDate(date)}
          minDate={new Date()}
          dateFormat="MMMM d, yyyy"
          filterDate={(date) => {
            const day = date.getDay();
            const year = date.getFullYear();
            const month = date.getMonth();
            const dateNum = date.getDate();

            const hasAskHerOutBouquet = cartItems.some(item => item.id === 29);
            if (hasAskHerOutBouquet) {
              if (year !== 2026 || month !== 1) return false;
              if (dateNum > 10) return false;
              return day === 3 || day === 6;
            }

            // Mother's Day products: only May 8-10, 2026
            const hasMothersDayProducts = cartItems.some(item => item.category === 'mothersday');
            if (hasMothersDayProducts) {
              return year === 2026 && month === 4 && dateNum >= 8 && dateNum <= 10;
            }

            const hasValentineProducts = cartItems.some(item => item.category === 'valentine');
            const isValentinePeriod = year === 2026 && month === 1 && (dateNum >= 12 && dateNum <= 14);

            if (isValentinePeriod) {
              if (hasValentineProducts) return true;
              return false;
            }

            if (hasValentineProducts) return false;

            return day === 3 || day === 6;
          }}
          className={`w-full px-3 md:px-4 py-2 md:py-3 border-2 rounded-lg focus:outline-none cursor-pointer text-sm md:text-base ${
            errors.deliveryDate ? 'border-red-500' : 'border-gray-200 focus:border-rose-600'
          }`}
          placeholderText="Select a date"
        />
        {errors.deliveryDate && <p className="text-red-500 text-xs md:text-sm mt-1">{errors.deliveryDate}</p>}
        {deliveryDate && (
          <p className="text-xs md:text-sm text-gray-600 mt-2">
            Selected: {format(deliveryDate, 'EEEE, MMMM d, yyyy')}
          </p>
        )}
      </div>

      <div>
        <label className="block text-gray-700 font-semibold mb-2 text-sm md:text-base">Delivery Time</label>
        {cartItems.some(item => item.category === 'mothersday') ? (
          <div className="bg-pink-50 border-2 border-pink-200 rounded-lg p-3 md:p-4">
            <p className="text-sm md:text-base text-rose-700 font-semibold">📩 Time Coordination Required</p>
            <p className="text-xs md:text-sm text-rose-600 mt-1">
              Please message us on Instagram <strong>@dearbea.flowers</strong> or email <strong>dearbea.flowers@gmail.com</strong> to coordinate your delivery time after placing your order.
            </p>
          </div>
        ) : (
          <select
            value={deliveryTime}
            onChange={(e) => setDeliveryTime(e.target.value)}
            className={`w-full px-3 md:px-4 py-2 md:py-3 border-2 rounded-lg focus:outline-none text-sm md:text-base ${
              errors.deliveryTime ? 'border-red-500' : 'border-gray-200 focus:border-rose-600'
            }`}
          >
            <option value="">Select time slot</option>
            {(() => {
              const isValentinesDay = deliveryDate && 
                deliveryDate.getFullYear() === 2026 && 
                deliveryDate.getMonth() === 1 && 
                deliveryDate.getDate() === 14;
              const hasValentineProducts = cartItems.some(item => item.category === 'valentine');
              let availableSlots = timeSlots;
              if (isValentinesDay && hasValentineProducts) {
                availableSlots = timeSlots.filter(slot => 
                  slot === '9:00 AM - 11:00 AM' || 
                  slot === '11:00 AM - 1:00 PM' || 
                  slot === '1:00 PM - 3:00 PM'
                );
              }
              return availableSlots.map((slot) => (
                <option key={slot} value={slot}>{slot}</option>
              ));
            })()}
          </select>
        )}
        {errors.deliveryTime && <p className="text-red-500 text-xs md:text-sm mt-1">{errors.deliveryTime}</p>}
        {deliveryDate && 
         deliveryDate.getFullYear() === 2026 && 
         deliveryDate.getMonth() === 1 && 
         deliveryDate.getDate() === 14 && 
         cartItems.some(item => item.category === 'valentine') && (
          <div className="mt-2 text-xs md:text-sm text-amber-600 bg-amber-50 p-2 rounded border border-amber-200">
            Valentine's Day deliveries only available before 4:00 PM
          </div>
        )}
      </div>
    </div>

    {getRushFee() > 0 && (
      <div className="mt-4 md:mt-6 bg-yellow-50 border-2 border-yellow-200 rounded-lg p-3 md:p-4">
        <div className="flex items-start gap-2 md:gap-3">
          <svg className="w-5 h-5 md:w-6 md:h-6 text-yellow-600 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
          </svg>
          <div>
            <p className="font-semibold text-yellow-800 text-sm md:text-base">Late Order Fee</p>
            <p className="text-yellow-700 text-xs md:text-sm">
              {deliveryDate && differenceInDays(deliveryDate, new Date()) < 3 && 
                "Orders within 72 hours: +25% late fee (subject to availability)"}
              {deliveryDate && differenceInDays(deliveryDate, new Date()) >= 3 && differenceInDays(deliveryDate, new Date()) < 7 && 
                "Orders within 7 days: +20% late fee (subject to availability)"}
              {deliveryDate && differenceInDays(deliveryDate, new Date()) >= 7 && differenceInDays(deliveryDate, new Date()) < 14 && 
                "Orders within 14 days: +10% late fee (subject to availability)"}
            </p>
          </div>
        </div>
      </div>
    )}
  </div>
)}

{/* ✅ Pickup Schedule */}
{deliveryMethod === 'pickup' && (
  <div className="bg-white rounded-xl md:rounded-2xl shadow-lg p-4 md:p-6 lg:p-8">
    <h2 className="text-xl md:text-2xl font-bold text-gray-900 mb-4 md:mb-6">Pickup Schedule</h2>
    
    <div className="bg-blue-50 border-2 border-blue-200 rounded-lg p-3 md:p-4 mb-4">
      <div className="flex items-start gap-2">
        <svg className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
        <div>
          <p className="text-sm md:text-base text-blue-800 font-semibold">
            {cartItems.some(item => item.category === 'mothersday')
              ? 'Mother\'s Day pickups: 8–10 May 2026 only'
              : 'Pickups available Wednesdays & Saturdays only'}
          </p>
          <p className="text-xs md:text-sm text-blue-700 mt-1">
            If your preferred date is unavailable, please contact us to inquire about special arrangements.
          </p>
        </div>
      </div>
    </div>
    
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
      <div>
        <label className="block text-gray-700 font-semibold mb-2 text-sm md:text-base">Pickup Date</label>
        <DatePicker
          selected={deliveryDate}
          onChange={(date) => setDeliveryDate(date)}
          minDate={new Date()}
          dateFormat="MMMM d, yyyy"
          filterDate={(date) => {
            const day = date.getDay();
            const year = date.getFullYear();
            const month = date.getMonth();
            const dateNum = date.getDate();

            // Mother's Day products: only May 8-10, 2026
            const hasMothersDayProducts = cartItems.some(item => item.category === 'mothersday');
            if (hasMothersDayProducts) {
              return year === 2026 && month === 4 && dateNum >= 8 && dateNum <= 10;
            }

            const hasValentineProducts = cartItems.some(item => item.category === 'valentine');
            if (hasValentineProducts) {
              return year === 2026 && month === 1 && dateNum >= 12 && dateNum <= 14;
            }

            if (!hasValentineProducts && year === 2026 && month === 1 && dateNum === 14) {
              return false;
            }

            return day === 3 || day === 6;
          }}
          className={`w-full px-3 md:px-4 py-2 md:py-3 border-2 rounded-lg focus:outline-none cursor-pointer text-sm md:text-base ${
            errors.deliveryDate ? 'border-red-500' : 'border-gray-200 focus:border-rose-600'
          }`}
          placeholderText="Select a date"
        />
        {errors.deliveryDate && <p className="text-red-500 text-xs md:text-sm mt-1">{errors.deliveryDate}</p>}
        {deliveryDate && (
          <p className="text-xs md:text-sm text-gray-600 mt-2">
            Selected: {format(deliveryDate, 'EEEE, MMMM d, yyyy')}
          </p>
        )}
      </div>

      <div>
        <label className="block text-gray-700 font-semibold mb-2 text-sm md:text-base">Pickup Time</label>
        {cartItems.some(item => item.category === 'mothersday') ? (
          <div className="bg-pink-50 border-2 border-pink-200 rounded-lg p-3 md:p-4">
            <p className="text-sm md:text-base text-rose-700 font-semibold">📩 Time Coordination Required</p>
            <p className="text-xs md:text-sm text-rose-600 mt-1">
              Please message us on Instagram <strong>@dearbea.flowers</strong> or email <strong>dearbea.flowers@gmail.com</strong> to coordinate your pickup time after placing your order.
            </p>
          </div>
        ) : (
          <select
            value={deliveryTime}
            onChange={(e) => setDeliveryTime(e.target.value)}
            className={`w-full px-3 md:px-4 py-2 md:py-3 border-2 rounded-lg focus:outline-none text-sm md:text-base ${
              errors.deliveryTime ? 'border-red-500' : 'border-gray-200 focus:border-rose-600'
            }`}
          >
            <option value="">Select time slot</option>
            {(() => {
              const isValentinesDay = deliveryDate && 
                deliveryDate.getFullYear() === 2026 && 
                deliveryDate.getMonth() === 1 && 
                deliveryDate.getDate() === 14;
              const hasValentineProducts = cartItems.some(item => item.category === 'valentine');
              let availableSlots = timeSlots;
              if (isValentinesDay && hasValentineProducts) {
                availableSlots = timeSlots.filter(slot => 
                  slot === '9:00 AM - 11:00 AM' || 
                  slot === '11:00 AM - 1:00 PM' || 
                  slot === '1:00 PM - 3:00 PM'
                );
              }
              return availableSlots.map((slot) => (
                <option key={slot} value={slot}>{slot}</option>
              ));
            })()}
          </select>
        )}
        {errors.deliveryTime && <p className="text-red-500 text-xs md:text-sm mt-1">{errors.deliveryTime}</p>}
        {deliveryDate && 
         deliveryDate.getFullYear() === 2026 && 
         deliveryDate.getMonth() === 1 && 
         deliveryDate.getDate() === 14 && 
         cartItems.some(item => item.category === 'valentine') && (
          <div className="mt-2 text-xs md:text-sm text-amber-600 bg-amber-50 p-2 rounded border border-amber-200">
            Valentine's Day pickups only available before 4:00 PM
          </div>
        )}
      </div>
    </div>

    <div className="mt-4 md:mt-6 bg-rose-50 border-2 border-rose-200 rounded-lg p-3 md:p-4">
      <div className="flex items-start gap-2 md:gap-3">
        <svg className="w-5 h-5 md:w-6 md:h-6 text-rose-600 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
        </svg>
        <div>
          <p className="font-semibold text-rose-800 mb-1 text-sm md:text-base">Pickup Location</p>
          <p className="text-rose-700 text-xs md:text-sm">Blacktown, Sydney NSW</p>
          <p className="text-xs md:text-sm text-rose-600 mt-1 md:mt-2">By appointment only</p>
        </div>
      </div>
    </div>
  </div>
)}


                {/* ✅ ADD THE TERMS CHECKBOX HERE - RIGHT BEFORE THE BUTTON */}

{/* ✅ TERMS & CONDITIONS CHECKBOX - REQUIRED */}
<div className="bg-white rounded-xl md:rounded-2xl shadow-lg p-4 md:p-6">
  <div className="flex items-start gap-2 md:gap-3">
    <input
      type="checkbox"
      id="termsAgree"
      checked={termsAccepted}
      onChange={(e) => setTermsAccepted(e.target.checked)}
      className="w-5 h-5 md:w-6 md:h-6 text-rose-600 rounded mt-0.5 flex-shrink-0 cursor-pointer"
    />
    <label htmlFor="termsAgree" className="text-sm md:text-base text-gray-700 cursor-pointer">
      I agree to the{' '}
      <button
        type="button"
        onClick={() => window.open('/terms', '_blank')}
        className="text-rose-600 hover:text-rose-700 font-semibold underline"
      >
        Terms & Conditions
      </button>
      {' '}
    </label>
  </div>
  
  {/* ✅ Error message if not checked */}
  {!termsAccepted && (
    <p className="text-xs md:text-sm text-gray-500 mt-2 ml-8 md:ml-9">
      You must agree to the Terms & Conditions to complete your order
    </p>
  )}
</div>


                <button
  onClick={handleContinueToPayment}
  disabled={calculatingFee}
  className={`w-full py-3 md:py-4 lg:py-5 rounded-lg md:rounded-xl text-base md:text-lg lg:text-xl font-bold transition-all duration-300 shadow-lg ${
    calculatingFee
      ? 'bg-gray-400 cursor-not-allowed text-white'
      : 'bg-rose-600 hover:bg-rose-700 hover:scale-105 text-white'
  }`}
>
  {calculatingFee ? 'Calculating...' : 'Continue to Payment'}
</button>
              </div>
            ) : (
              // STEP 2: Payment
              <div className="space-y-6 md:space-y-8">
                {clientSecret ? (
                  <div className="bg-white rounded-xl md:rounded-2xl shadow-lg p-4 md:p-6 lg:p-8">
                    <h2 className="text-xl md:text-2xl font-bold text-gray-900 mb-4 md:mb-6">Payment Details</h2>
                    
                    <Elements stripe={stripePromise} options={options}>
                      <PaymentForm
                        amount={total}
                        onSuccess={handlePaymentSuccess}
                        customerInfo={formData}
                      />
                    </Elements>
                  </div>
                ) : (
                  <div className="bg-white rounded-xl md:rounded-2xl shadow-lg p-8 md:p-12 flex items-center justify-center">
                    <div className="text-center">
                      <svg className="animate-spin h-10 w-10 md:h-12 md:w-12 text-rose-600 mx-auto mb-4" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                      </svg>
                      <p className="text-gray-600 text-sm md:text-base">Preparing payment...</p>
                    </div>
                  </div>
                )}

                <button
                  onClick={() => setCurrentStep(1)}
                  className="w-full border-2 border-gray-300 hover:border-rose-600 text-gray-700 py-3 md:py-4 rounded-lg md:rounded-xl text-base md:text-lg font-semibold transition"
                >
                  ← Back to Details
                </button>
              </div>
            )}
          </div>

          {/* ✅ Right side - Order Summary - Responsive */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-xl md:rounded-2xl shadow-lg p-4 md:p-6 lg:p-10 lg:sticky lg:top-24 min-h-[200px] md:min-h-[300px]">
              <h2 className="text-xl md:text-2xl font-bold text-gray-900 mb-4 md:mb-6 lg:mb-8">Order Summary</h2>
              
              <div className="space-y-3 md:space-y-4 mb-4 md:mb-6 max-h-48 md:max-h-60 overflow-y-auto">
                {cartItems.map((item, index) => (
                  <div key={`${item.id}-${index}`} className="flex gap-2 md:gap-4">
                    <img
                      src={item.image}
                      alt={item.name}
                      className="w-12 h-12 md:w-14 md:h-14 lg:w-16 lg:h-16 object-cover rounded-lg flex-shrink-0"
                    />
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold text-gray-900 text-xs md:text-sm truncate">{item.name}</p>
                      {item.customization?.color && (
                        <p className="text-xs text-gray-600">Color: {item.customization.color}</p>
                      )}
                      <p className="text-xs md:text-sm text-gray-600">Qty: {item.quantity}</p>
                    </div>
                    <p className="font-semibold text-gray-900 text-xs md:text-sm">{item.price}</p>
                  </div>
                ))}
              </div>

              <div className="border-t pt-3 md:pt-4 space-y-2 md:space-y-3">
                <div className="flex justify-between text-gray-700 text-sm md:text-base">
                  <span>Subtotal</span>
                  <span>${subtotal.toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-gray-700 text-sm md:text-base">
                  <span>Delivery Fee</span>
                  <span>${deliveryFee.toFixed(2)}</span>
                </div>
                {rushFee > 0 && (
                  <div className="flex justify-between text-yellow-700 text-sm md:text-base">
                    <span>Rush Order Fee</span>
                    <span>+${rushFee.toFixed(2)}</span>
                  </div>
                )}
                <div className="border-t pt-2 md:pt-3 flex justify-between text-lg md:text-xl font-bold text-gray-900">
                  <span>Total</span>
                  <span className="text-rose-600">${total.toFixed(2)}</span>
                </div>
              </div>
            </div>
          </div>

        </div>
      </div>
          {/* ✅✅✅ ADD THIS MODAL HERE - RIGHT BEFORE THE FINAL </div> ✅✅✅ */}

    {/* ✅✅✅ FORM ERROR MODAL - ADD THIS ✅✅✅ */}
{showFormError && (
  <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
    <div className="bg-white rounded-2xl md:rounded-3xl max-w-md w-full p-6 md:p-8 shadow-2xl max-h-[90vh] overflow-y-auto">
      {/* Error Icon */}
      <div className="flex justify-center mb-4 md:mb-6">
        <div className="w-16 h-16 md:w-20 md:h-20 bg-red-100 rounded-full flex items-center justify-center">
          <svg className="w-10 h-10 md:w-12 md:h-12 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
          </svg>
        </div>
      </div>

      {/* Title */}
      <h3 className="text-xl md:text-2xl font-bold text-gray-900 text-center mb-3 md:mb-4">
        Incomplete Information
      </h3>

      {/* Message */}
      <p className="text-sm md:text-base text-gray-600 text-center mb-4">
        Please complete all required fields before proceeding to payment.
      </p>

      {/* Missing Fields List */}
      {missingFields.length > 0 && (
        <div className="bg-red-50 rounded-lg p-4 mb-6">
          <p className="text-sm font-semibold text-red-800 mb-2">Missing or Invalid Fields:</p>
          <ul className="space-y-1">
            {missingFields.map((field, index) => (
              <li key={index} className="text-sm text-red-700 flex items-start gap-2">
                <span className="text-red-500 mt-0.5">•</span>
                <span>{field}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Close Button */}
      <button
        onClick={() => {
          setShowFormError(false);
          // Auto-scroll to first error after closing modal
          setTimeout(() => {
            const firstErrorField = Object.keys(errors)[0];
            const element = document.querySelector(`[name="${firstErrorField}"]`) || 
                          document.getElementById('termsAgree');
            element?.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }, 100);
        }}
        className="w-full bg-rose-600 hover:bg-rose-700 text-white py-3 md:py-4 rounded-xl font-semibold transition text-sm md:text-base"
      >
        Got it, I'll complete the form
      </button>
    </div>
  </div>
)}

    {showTermsError && (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-2xl md:rounded-3xl max-w-md w-full p-6 md:p-8 shadow-2xl">
          {/* Warning Icon */}
          <div className="flex justify-center mb-4 md:mb-6">
            <div className="w-16 h-16 md:w-20 md:h-20 bg-yellow-100 rounded-full flex items-center justify-center">
              <svg className="w-10 h-10 md:w-12 md:h-12 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
            </div>
          </div>

          {/* Title */}
          <h3 className="text-xl md:text-2xl font-bold text-gray-900 text-center mb-3 md:mb-4">
            Agreement Required
          </h3>

          {/* Message */}
          <p className="text-sm md:text-base text-gray-600 text-center mb-6 md:mb-8">
            You must agree to our Terms & Conditions to continue with your order.
          </p>

          {/* Action Buttons */}
          <div className="space-y-3">
            <button
              onClick={() => {
                setShowTermsError(false);
                window.open('/terms', '_blank');
              }}
              className="w-full bg-rose-600 hover:bg-rose-700 text-white py-3 md:py-4 rounded-xl font-semibold transition text-sm md:text-base"
            >
              📜 Read Terms & Conditions
            </button>
            <button
              onClick={() => setShowTermsError(false)}
              className="w-full bg-gray-100 hover:bg-gray-200 text-gray-800 py-3 md:py-4 rounded-xl font-semibold transition text-sm md:text-base"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    )}

    {/* ✅ SUCCESS MODAL WITH CONFETTI */}
    {showSuccessModal && (
      <>
        {/* Confetti */}
        {showConfetti && (
          <Confetti
            width={window.innerWidth}
            height={window.innerHeight}
            recycle={false}
            numberOfPieces={500}
            gravity={0.3}
            initialVelocityY={-20}  // ✅ Shoots upward!
            colors={['#e11d48', '#f43f5e', '#fb7185', '#fda4af', '#fecdd3', '#fff1f2']}  // ✅ Pink/rose themed!
        />
      )}
        
        {/* Success Modal */}
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-3xl max-w-lg w-full p-8 md:p-12 shadow-2xl text-center transform animate-bounce-in">
            
            {/* Success Icon */}
            <div className="flex justify-center mb-6">
              <div className="w-24 h-24 bg-green-100 rounded-full flex items-center justify-center animate-scale-in">
                <svg className="w-12 h-12 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                </svg>
              </div>
            </div>

            {/* Success Message */}
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              🎉 Order Placed Successfully!
            </h2>
            
            <p className="text-lg text-gray-600 mb-6">
              Thank you for your order!
            </p>

            {/* Order Number */}
            <div className="bg-rose-50 border-2 border-rose-200 rounded-2xl p-6 mb-6">
              <p className="text-sm text-gray-600 mb-2">Your Order Number</p>
              <p className="text-3xl font-bold text-rose-600 tracking-wider">
                #{orderNumber}
              </p>
            </div>

            {/* Email Confirmation */}
            <div className="bg-blue-50 border-2 border-blue-200 rounded-xl p-4 mb-8">
              <div className="flex items-center justify-center gap-2 text-blue-800">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
                <p className="text-sm font-medium">
                  A confirmation email has been sent to <span className="font-bold">{formData.email}</span>
                </p>
              </div>
            </div>

            {/* Continue Button */}
            <button
              onClick={() => {
                setShowSuccessModal(false);
                clearCart();  // ✅ Clear cart HERE instead
                navigate('/');
              }}
              className="w-full bg-gradient-to-r from-rose-500 to-pink-500 hover:from-rose-600 hover:to-pink-600 text-white py-4 rounded-xl text-lg font-bold transition-all duration-300 transform hover:scale-105 shadow-lg"
            >
              Continue Shopping
            </button>
          </div>
        </div>
      </>
    )}
    {/* ✅ DELIVERY FEE CALCULATION SUCCESS MODAL */}
{showDeliveryFeeModal && deliveryFeeCalculated && deliveryDistance !== null && (
  <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
    <div className="bg-white rounded-2xl md:rounded-3xl max-w-md w-full p-6 md:p-8 shadow-2xl">
      
      {/* Success Icon */}
      <div className="flex justify-center mb-4 md:mb-6">
        <div className="w-16 h-16 md:w-20 md:h-20 bg-green-100 rounded-full flex items-center justify-center">
          <svg className="w-10 h-10 md:w-12 md:h-12 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        </div>
      </div>

      {/* Title */}
      <h3 className="text-xl md:text-2xl font-bold text-gray-900 text-center mb-3 md:mb-4">
        Delivery Available!
      </h3>

      {/* Distance Info */}
      <div className="bg-gray-50 rounded-xl p-4 mb-4">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm text-gray-600">Distance from Blacktown:</span>
          <span className="text-lg font-bold text-gray-900">{deliveryDistance.toFixed(1)} km</span>
        </div>
        <div className="flex items-center justify-between">
          <span className="text-sm text-gray-600">Rate:</span>
          <span className="text-sm font-semibold text-gray-700">$2.00 per km</span>
        </div>
      </div>

      {/* Delivery Fee Display */}
      <div className="bg-gradient-to-r from-rose-50 to-pink-50 border-2 border-rose-200 rounded-2xl p-6 mb-6">
        <p className="text-sm text-gray-600 text-center mb-2">Your Delivery Fee</p>
        <p className="text-4xl md:text-5xl font-bold text-rose-600 text-center">
          ${deliveryFeeAmount.toFixed(2)}
        </p>
        {deliveryFeeAmount === 0 && (
          <p className="text-center text-green-600 font-bold mt-2 text-lg">
            FREE DELIVERY!
          </p>
        )}
      </div>

      {/* Success Message */}
      <div className="bg-blue-50 border-2 border-blue-200 rounded-xl p-4 mb-6">
        <p className="text-sm text-blue-800 text-center">
          Great news! We can deliver to your address. This fee has been added to your order total.
        </p>
      </div>

      {/* Close Button */}
      <button
        onClick={() => setShowDeliveryFeeModal(false)}
        className="w-full bg-rose-600 hover:bg-rose-700 text-white py-3 md:py-4 rounded-xl font-semibold transition text-sm md:text-base shadow-lg hover:shadow-xl transform hover:scale-105"
      >
        Perfect! Continue with Order
      </button>
    </div>
  </div>
)}
  </div>
);
};

export default Checkout;